import { SITE_REGION_CODES, type SiteRegionCode } from './utils';

// Static assets / downloads / system paths that must never have a regional prefix
const NON_REGIONAL_PREFIXES = [
  '/assets/',
  'assets/',
  '/_next/',
  '/api/',
  '/icons/',
  '/fonts/',
  '/images/',
  '/robots.txt',
  '/sitemap.xml',
  '/maintenance.html',
  '/favicon.ico',
];

const DOWNLOAD_FILE_EXTENSIONS =
  /\.(pdf|zip|tar|gz|docx?|xlsx?|pptx?|csv|png|jpe?g|svg|webp|gif|ico|mp4|mov|mp3|wav|exe|dmg)(\?.*)?$/i;

const INTERNAL_HOSTS = new Set([
  'unimig.com',
  'www.unimig.com',
  'unimig.com.au',
  'www.unimig.com.au',
]);

/**
 * Returns a region-localized URL for internal links.
 *
 * - For AU ('au'): internal paths remain unprefixed ('/product/foo', '/', etc.)
 * - For NZ ('nz'): internal paths are prefixed with '/nz' ('/nz/product/foo', '/nz/', etc.)
 * - For IN ('in'): internal paths are prefixed with '/in' ('/in/product/foo', '/in/', etc.)
 * - For US ('us'): homepage points to '/us/coming-soon/', internal paths to '/us/...'
 *
 * Preserves query strings (?query=...) and hash fragments (#sku=...).
 * Exempts external links, mailto/tel, and file downloads.
 */
export function getRegionalUrl(
  path: string | null | undefined,
  regionCode?: string | null
): string {
  if (!path) return '';

  const trimmed = path.trim();
  if (!trimmed) return '';

  // Anchors, special protocols
  if (
    trimmed.startsWith('#') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:') ||
    trimmed.startsWith('javascript:') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }

  // Handle absolute URLs
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (INTERNAL_HOSTS.has(url.hostname.toLowerCase())) {
        return getRegionalUrl(url.pathname + url.search + url.hash, regionCode);
      }
      return trimmed;
    } catch {
      return trimmed;
    }
  }

  // Check static / download exemptions
  for (const prefix of NON_REGIONAL_PREFIXES) {
    if (trimmed.startsWith(prefix)) {
      return trimmed;
    }
  }

  if (DOWNLOAD_FILE_EXTENSIONS.test(trimmed)) {
    return trimmed;
  }

  // Determine active target region
  const code = (regionCode || 'au').toLowerCase();
  const region: SiteRegionCode = (
    SITE_REGION_CODES as readonly string[]
  ).includes(code)
    ? (code as SiteRegionCode)
    : 'au';

  // Split path from query string & hash fragment
  const match = trimmed.match(/^([^?#]*)(.*)$/);
  let rawPath = match ? match[1] : trimmed;
  const suffix = match ? match[2] : '';

  if (!rawPath.startsWith('/')) {
    rawPath = '/' + rawPath;
  }

  // Extract segments and check if first segment is an existing region code
  const segments = rawPath.split('/').filter(Boolean);
  const firstSeg = segments[0]?.toLowerCase();
  const isFirstSegRegion =
    firstSeg && (SITE_REGION_CODES as readonly string[]).includes(firstSeg);

  const rawSubPath = isFirstSegRegion
    ? '/' + segments.slice(1).join('/')
    : rawPath;
  const cleanSubPath = rawSubPath === '/' ? '' : rawSubPath.replace(/\/+$/, '');
  const hadTrailingSlash =
    rawPath.endsWith('/') || (isFirstSegRegion && segments.length === 1);

  // Root / Homepage handling
  if (!cleanSubPath) {
    if (region === 'au') return `/${suffix}`;
    if (region === 'us') return `/us/coming-soon/${suffix}`;
    return `/${region}/${suffix}`;
  }

  // Next.js trailingSlash: true canonical format: page routes end with /
  const isFilePath = /\.[a-zA-Z0-9]+$/.test(cleanSubPath);
  const slash = !isFilePath || hadTrailingSlash ? '/' : '';

  // US coming-soon special handling
  if (region === 'us') {
    if (cleanSubPath === '/coming-soon') {
      return `/us/coming-soon/${suffix}`;
    }
    return `/us${cleanSubPath}${slash}${suffix}`;
  }

  // AU single-region domain (no prefix)
  if (region === 'au') {
    return `${cleanSubPath}${slash}${suffix}`;
  }

  // Multi-region (nz, in)
  return `/${region}${cleanSubPath}${slash}${suffix}`;
}

/**
 * Parses an HTML string and localizes all internal <a href="..."> links
 * to the specified region.
 */
export function localizeHtmlLinks(
  html: string | undefined | null,
  regionCode?: string | null
): string {
  if (!html || typeof html !== 'string') return html ?? '';
  const code = (regionCode || 'au').toLowerCase();
  if (code === 'au') return html;

  return html.replace(
    /(<a\b[^>]*?\bhref\s*=\s*)(["'])(.*?)\2([^>]*?>)/gi,
    (match, prefix, quote, href, suffix) => {
      const localized = getRegionalUrl(href, code);
      return `${prefix}${quote}${localized}${quote}${suffix}`;
    }
  );
}
